import { useState, useCallback } from 'react';
import { apiClient } from '../config/api';

export const useReports = (user, token) => {
  // Reports state
  const [reportsData, setReportsData] = useState({});
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsFrom, setReportsFrom] = useState(null);
  const [reportsTo, setReportsTo] = useState(null);
  const [selectedReport, setSelectedReport] = useState(() => {
    if (user && (user.role === 'admin' || user.role === 'superadmin')) {
      return 'overview';
    }
    return 'daily';
  });
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsFrom, setAnalyticsFrom] = useState(null);
  const [analyticsTo, setAnalyticsTo] = useState(null);
  const [priorityVolume, setPriorityVolume] = useState([]);
  const [dailyWorkData, setDailyWorkData] = useState([]);

  // Fetch reports data
  const fetchReportsData = useCallback(async () => {
    if (!token) return;
    setReportsLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (reportsFrom) queryParams.append('from_date', reportsFrom.toISOString());
      if (reportsTo) queryParams.append('to_date', reportsTo.toISOString());
      if (selectedReport) queryParams.append('report_type', selectedReport);

      const response = await apiClient(`/api/reports/?${queryParams.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setReportsData(data);
      }
    } catch (error) {
      console.error('Fetch reports error:', error);
    } finally {
      setReportsLoading(false);
    }
  }, [token, reportsFrom, reportsTo, selectedReport]);

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    setAnalyticsLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (analyticsFrom) queryParams.append('from_date', analyticsFrom.toISOString());
      if (analyticsTo) queryParams.append('to_date', analyticsTo.toISOString());

      const response = await apiClient(`/api/analytics/?${queryParams.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
        if (data.priority_volume) {
          setPriorityVolume(data.priority_volume);
        }
      }
    } catch (error) {
      console.error('Fetch analytics error:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [token, analyticsFrom, analyticsTo]);

  // Fetch daily work breakdown
  const fetchDailyWorkData = useCallback(async () => {
    if (!token) return;
    try {
      const response = await apiClient('/api/analytics/daily-work');
      if (response.ok) {
        const data = await response.json();
        setDailyWorkData(data);
      }
    } catch (error) {
      console.error('Fetch daily work error:', error);
    }
  }, [token]);

  return {
    reportsData,
    reportsLoading,
    reportsFrom,
    setReportsFrom,
    reportsTo,
    setReportsTo,
    selectedReport,
    setSelectedReport,
    autoRefresh,
    setAutoRefresh,
    analyticsData,
    analyticsLoading,
    analyticsFrom,
    setAnalyticsFrom,
    analyticsTo,
    setAnalyticsTo,
    priorityVolume,
    dailyWorkData,
    fetchReportsData,
    fetchAnalytics,
    fetchDailyWorkData,
  };
};
